/**
 * Resolves the tenant slug from a hostname.
 * 
 * @param hostname - The hostname (e.g., "stann.example.com" or "localhost:3000")
 * @returns The tenant slug, empty string for apex domains, or default slug for localhost
 */
/**
 * Returns true when the hostname is the super-admin subdomain.
 */
export function isAdminSubdomain(hostname: string): boolean {
  const h = hostname.split(':')[0].toLowerCase();
  return h === 'admin.thewaymaker.app' || h === 'admin.localhost';
}

/**
 * Extracts the active client slug from an admin-subdomain pathname.
 * e.g. "/admin/stann/zones" → "stann"
 */
export function extractClientSlugFromPath(pathname: string): string {
  const match = pathname.match(/^\/admin\/([^/]+)/);
  return match?.[1] ?? '';
}

export function resolveTenantSlug(hostname: string): string {
  // Remove port if present (e.g., "localhost:3000" -> "localhost")
  const hostnameWithoutPort = hostname.split(':')[0].toLowerCase();

  // Reserve the admin subdomain — no automatic tenant resolution
  if (isAdminSubdomain(hostnameWithoutPort)) {
    return '';
  }

  // Handle apex domains - return empty string (no tenant)
  if (hostnameWithoutPort === 'thewaymaker.app' || hostnameWithoutPort === 'www.thewaymaker.app') {
    return '';
  }
  
  // Split by dots to check for subdomain
  const parts = hostnameWithoutPort.split('.');
  
  // Handle localhost subdomains for development (e.g., stann.localhost)
  if (hostnameWithoutPort.endsWith('.localhost') || hostnameWithoutPort.endsWith('.127.0.0.1')) {
    // Extract tenant slug from subdomain (e.g., "stann.localhost" -> "stann")
    if (parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www') {
      return parts[0];
    }
    // Plain localhost - return default slug
    return process.env.NEXT_PUBLIC_DEFAULT_ORG_SLUG || 'stann';
  }
  
  // Handle plain localhost - return default slug (for development)
  if (hostnameWithoutPort === 'localhost' || hostnameWithoutPort === '127.0.0.1') {
    return process.env.NEXT_PUBLIC_DEFAULT_ORG_SLUG || 'stann';
  }
  
  // If there are fewer than 2 parts, no subdomain (e.g., "example")
  // Return empty string (no tenant)
  if (parts.length < 2) {
    return '';
  }
  
  // If hostname ends with .thewaymaker.app, check if it's a tenant subdomain
  if (hostnameWithoutPort.endsWith('.thewaymaker.app')) {
    // If it's apex domain, already handled above
    // Otherwise, it's a tenant subdomain - extract slug
    if (parts.length >= 3) {
      return parts[0]; // First part is the tenant slug
    }
    return '';
  }
  
  // For other domains (2 parts like "example.com"), return empty string (no tenant)
  // This allows marketing routes to work without tenant
  if (parts.length === 2) {
    return '';
  }
  
  // If there are 3+ parts (e.g., "stann.example.com"), first part is the slug
  // Return the first part as the slug
  return parts[0];
}
